import React, { useState, useEffect } from 'react';
import api from '../../api';
import { useSeason } from '../../context/SeasonContext';
import { useMobile } from '../../context/MobileContext';
import './LeagueMetrics.css';
import PropTypes from 'prop-types';

// Import chart components
import LossTrackingChart from './charts/LossTrackingChart';
import DistributionCards from './charts/DistributionCards';
import PickDistributionChart from './charts/PickDistributionChart';
import WeeklyPerformanceChart from './charts/WeeklyPerformanceChart';
import UserPerformanceChart from './charts/UserPerformanceChart';
import LeadTimeAnalysis from './charts/LeadTimeAnalysis';
import PickTypePerformance from './charts/PickTypePerformance';
import PickLocationAnalysis from './charts/PickLocationAnalysis';
import SeasonalPerformanceChart from './charts/SeasonalPerformanceChart';

// Import mobile components
import MobileLeagueStats from '../mobile/MobileLeagueStats';

const LeagueMetrics = ({ leaderboardData, season }) => {
    const [metrics, setMetrics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isMobile } = useMobile();
    const { effectiveSeason } = useSeason();
    const selectedSeason = season || effectiveSeason;

    useEffect(() => {
        const fetchMetrics = async () => {
            try {
                const response = await api.getLeagueMetrics(selectedSeason);
                setMetrics(response.data);
            } catch (err) {
                setError('Failed to load metrics');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchMetrics();
    }, [selectedSeason]);

    if (loading) return <div className="metrics-loading">Loading metrics...</div>;
    if (error) return <div className="metrics-error">{error}</div>;
    if (!metrics) return null;

    // Mobile view - use dedicated mobile component
    if (isMobile) {
        return <MobileLeagueStats season={selectedSeason} />;
    }

    // Handle season not started case
    if (!metrics.seasonStarted) {
        return (
            <div className="metrics-dashboard">
                <div className="season-not-started">
                    <h3>{metrics.message}</h3>
                </div>
            </div>
        );
    }

    const renderSummaryMetrics = () => (
        <div className="metrics-summary">
            <div className="metric-card">
                <h3>Total Picks</h3>
                <div className="metric-value">{metrics.summary.totalPicks}</div>
                <div className="metric-detail">
                    {metrics.summary.completedPicks} completed, {metrics.summary.bye_picks} byes, {metrics.summary.pendingPicks} pending
                </div>
            </div>
            <div className="metric-card">
                <h3>Win Rate</h3>
                <div className="metric-value">
                    {metrics.summary.winRate ? metrics.summary.winRate : '0.0'}%
                </div>
                <div className="metric-detail">
                    of {metrics.summary.completedPicks} completed picks
                </div>
            </div>
            <div className="metric-card">
                <h3>Total Wins</h3>
                <div className="metric-value">{metrics.summary.totalWins}</div>
            </div>
            <div className="metric-card">
                <h3>League Record</h3>
                <div className="metric-value">
                    {metrics.summary.totalWins}-{metrics.summary.totalLosses}
                </div>
                <div className="metric-detail">
                    ({metrics.summary.pendingPicks} games pending)
                </div>
            </div>
        </div>
    );

    // Handle case where leaderboardData might not be an array (e.g., when season hasn't started)
    const users = Array.isArray(leaderboardData) ? leaderboardData : (leaderboardData?.users || []);

    const leadTimeData = users.map(user => {
        const userMetrics = metrics?.leadTimeStats?.find?.(m => m.username === user.username) || {};

        const totalGames = (user.wins || 0) + (user.losses || 0);
        const winPercentage = totalGames > 0 ? ((user.wins || 0) / totalGames) * 100 : 0;

        return {
            username: user.username,
            avg_lead_time: userMetrics?.avg_lead_time ?? 0,
            avg_winning_lead_time: userMetrics?.avg_winning_lead_time ?? 0,
            avg_losing_lead_time: userMetrics?.avg_losing_lead_time ?? 0,
            rank: user.rank,
            win_percentage: winPercentage,
            wins: user.wins || 0,
            losses: user.losses || 0
        };
    });

    const sortedLeadTimeData = [...leadTimeData].sort((a, b) => {
        const rankA = parseInt(String(a.rank).replace('T', ''));
        const rankB = parseInt(String(b.rank).replace('T', ''));
        return rankA - rankB;
    });

    console.log('Final leadTimeData:', leadTimeData);


    const renderDesktopCharts = () => (
        <div className="metrics-charts">
            <LossTrackingChart data={metrics.lossTracking} />
            <DistributionCards data={metrics.distributionSplits} />
            <PickDistributionChart data={metrics.pickDistribution} />
            <WeeklyPerformanceChart data={metrics.weeklyTrends} />
            <SeasonalPerformanceChart data={metrics.weeklyTrends} />
            <UserPerformanceChart data={metrics.userPerformance} />
            <LeadTimeAnalysis data={sortedLeadTimeData} />
            <PickTypePerformance data={metrics.pickTypePerformance} />
            <PickLocationAnalysis data={metrics.pickLocationAnalysis} />
        </div>
    );

    return (
        <div className="metrics-dashboard">
            {renderSummaryMetrics()}
            {renderDesktopCharts()}
        </div>
    );
};

LeagueMetrics.propTypes = {
    leaderboardData: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.shape({
            username: PropTypes.string.isRequired,
            wins: PropTypes.number,
            losses: PropTypes.number,
            rank: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ])
        })),
        PropTypes.shape({
            users: PropTypes.arrayOf(PropTypes.shape({
                username: PropTypes.string.isRequired,
                wins: PropTypes.number,
                losses: PropTypes.number,
                rank: PropTypes.oneOfType([
                    PropTypes.number,
                    PropTypes.string
                ])
            }))
        })
    ]).isRequired,
    season: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ])
};

export default LeagueMetrics;
